// Zielparameter-Sektion über dem eingebetteten Notebook (Issue #355).
//
// Drei Dinge an einer Stelle:
//
//   1. Auswahl der Optimierungsziele. Die WÄHLBAREN Ziele kommen aus den `mpc-optimizer`-Knoten
//      des Projektgraphen (das Backend liefert sie als `available` mit) — es gibt keine zweite
//      Liste, die auseinanderlaufen könnte.
//   2. Begründungsfeld: warum genau diese Ziele. Persistiert und git-versioniert, weil bei einer
//      Anlage die Begründung einer Gewichtung genauso prüfbar ist wie die Zahl selbst.
//   3. Knopf „Training starten": löst einen Lauf mit den GESPEICHERTEN Zielparametern aus.
//
// Bewusst getrennt: Speichern und Starten sind zwei Knöpfe. Ein Lauf, der beim Klick nebenbei
// noch die Konfiguration ändert, macht bei einem Fehlschlag unklar, was gilt — und der Lauf
// bezieht sich immer auf das, was im Anlagenmodell steht, nicht auf einen ungespeicherten
// Formularzustand. Ungespeicherte Änderungen werden deshalb angezeigt und blockieren den Start.

import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  generateNotebook,
  getTrainingGoals,
  runGeneratedNotebook,
  setTrainingGoals,
  startTrainingRun,
} from '../api/client';
import type { NotebookGenerateResult, OptimizerGoal, TrainingGoalEntry } from '../api/types';
import { anzeigeName } from '../graph/anzeige';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { Button, PanelHeading } from '../components/ui';

// Das Notebook, auf das sich die Zielparameter beziehen. Die Trainings-Ansicht bettet heute die
// ganze Lab-Oberfläche in EINEM iframe ein; ein Notebook je Eintrag ist trotzdem richtig
// modelliert, damit eine Anlage später mehrere Notebooks mit eigenen Zielen tragen kann.
const DEFAULT_NOTEBOOK = 'demo_mpc_forward.ipynb';

type Meldung = { kind: 'ok' | 'fehler'; text: string } | null;

// Ein Ziel in Klartext: „Gasertrag (m³/h) — maximieren, Gewicht 3". Ohne diese Zeile stünde in
// der Auswahl nur „FQ-107@d60", und niemand wüsste, worüber er entscheidet.
function zielBeschriftung(
  z: OptimizerGoal,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  // Kurzlabel (#373), sonst der Bezeichner. Den Beleg hängt die Liste selbst an — der
  // Optimierer-Ausgang steht dort als „(FQ-107@d60)" mit dem Horizont, der zur Größe gehört.
  const kopf = anzeigeName(z);
  const einheit = z.unit ? ` [${z.unit}]` : '';
  const art =
    z.goal === 'setpoint'
      ? t('training.goals.art.setpoint', { wert: String(z.setpoint ?? '') })
      : z.goal === 'min'
        ? t('training.goals.art.min')
        : t('training.goals.art.max');
  return `${kopf}${einheit} — ${art}, ${t('training.goals.gewicht')} ${z.weight}`;
}

export function TrainingGoalsPanel(): JSX.Element | null {
  const { t } = useI18n();
  const { activeProjectId } = useStore();
  const [verfuegbar, setVerfuegbar] = useState<OptimizerGoal[]>([]);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [notiz, setNotiz] = useState('');
  // Der zuletzt GESPEICHERTE Stand — daran erkennen wir ungespeicherte Änderungen.
  const [gespeichert, setGespeichert] = useState<{ selected: string[]; note: string } | null>(null);
  const [laden, setLaden] = useState(true);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [meldung, setMeldung] = useState<Meldung>(null);
  const [offen, setOffen] = useState(true);
  // Das zuletzt ERZEUGTE Notebook. Es liegt als Snapshot, ist aber noch nicht gelaufen: zwischen
  // „ein Modell hat Code geschrieben" und „er läuft auf der Appliance" gehört ein Mensch, der ihn
  // gesehen hat. Deshalb wird der Code hier gezeigt und der Lauf ist ein zweiter Klick.
  const [erzeugt, setErzeugt] = useState<NotebookGenerateResult | null>(null);

  const laden_ = useCallback(async (projectId: string) => {
    setLaden(true);
    try {
      const doc = await getTrainingGoals(projectId);
      const eintrag = doc.entries.find((e) => e.notebook === DEFAULT_NOTEBOOK);
      setVerfuegbar(doc.available);
      setGewaehlt(eintrag?.selected ?? []);
      setNotiz(eintrag?.note ?? '');
      setGespeichert({ selected: eintrag?.selected ?? [], note: eintrag?.note ?? '' });
      setMeldung(null);
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    if (activeProjectId) void laden_(activeProjectId);
  }, [activeProjectId, laden_]);

  if (!activeProjectId) return null;

  // Ziele gewählt, aber nicht begründet (#398). Der Start wird VOR dem Lauf abgewiesen, mit
  // Grund — nicht danach: ein Trainingslauf, der erst nach zwanzig Minuten an einer fehlenden
  // Notiz scheitert, hätte GPU-Zeit gekostet und nichts erklärt. Das Backend weist denselben Fall
  // ebenfalls ab; hier steht die Erklärung, dort der Riegel.
  const begruendungFehlt = gewaehlt.length > 0 && notiz.trim().length === 0;

  const geaendert =
    gespeichert !== null &&
    (gespeichert.note !== notiz ||
      gespeichert.selected.length !== gewaehlt.length ||
      gespeichert.selected.some((s) => !gewaehlt.includes(s)));

  function umschalten(output: string): void {
    setGewaehlt((vorher) =>
      vorher.includes(output) ? vorher.filter((o) => o !== output) : [...vorher, output],
    );
  }

  async function speichern(): Promise<void> {
    if (!activeProjectId) return;
    setBeschaeftigt(true);
    try {
      const eintrag: TrainingGoalEntry = {
        notebook: DEFAULT_NOTEBOOK,
        selected: gewaehlt,
        note: notiz,
      };
      await setTrainingGoals(activeProjectId, [eintrag]);
      setGespeichert({ selected: [...gewaehlt], note: notiz });
      setMeldung({ kind: 'ok', text: t('training.goals.gespeichert') });
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function trainingStarten(): Promise<void> {
    setBeschaeftigt(true);
    try {
      // Die gewählten Ziele gehen als Lauf-Parameter mit; im Pod kommen sie als
      // BFF_PARAM_<NAME> an. Die Begründung fährt mit, damit sie im Lauf-Protokoll steht und
      // nicht nur im Projekt-Repo — wer den Lauf später ansieht, liest dort mit, warum.
      const run = await startTrainingRun({
        notebook: DEFAULT_NOTEBOOK,
        parameters: {
          project: activeProjectId,
          goals: gewaehlt,
          goals_note: notiz,
        },
      });
      setMeldung({ kind: 'ok', text: t('training.goals.gestartet', { id: run.runId }) });
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function notebookGenerieren(): Promise<void> {
    if (!activeProjectId) return;
    setBeschaeftigt(true);
    try {
      const doc = await generateNotebook(activeProjectId, { notebook: 'mpc_generated.ipynb' });
      setErzeugt(doc);
      setMeldung({ kind: 'ok', text: t('training.goals.erzeugt', { model: doc.model }) });
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function notebookAusfuehren(): Promise<void> {
    if (!activeProjectId || !erzeugt) return;
    setBeschaeftigt(true);
    try {
      const doc = await runGeneratedNotebook(activeProjectId, { sha256: erzeugt.sha256 });
      setMeldung({
        kind: doc.ok ? 'ok' : 'fehler',
        // Auch im Erfolgsfall zählt, ob wirklich ein Artefakt entstand — „lief durch" heißt
        // nicht „hat etwas erzeugt".
        text: `${doc.ok ? t('training.goals.laufOk') : t('training.goals.laufFehler')} ${
          doc.graphNote ?? ''
        }`,
      });
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  return (
    <section
      className="border-b border-bf-border px-4 py-3"
      data-testid="training-goals-panel"
      aria-label={t('training.goals.heading')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <PanelHeading>{t('training.goals.heading')}</PanelHeading>
          <p className="text-sm text-bf-muted">{t('training.goals.intro')}</p>
        </div>
        <Button testId="training-goals-toggle" onClick={() => setOffen((o) => !o)}>
          {offen ? t('training.goals.einklappen') : t('training.goals.ausklappen')}
        </Button>
      </div>

      {offen && (
        <div className="mt-3 grid gap-4 lg:grid-cols-2">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-bf-muted">
              {t('training.goals.auswahl')}
            </h4>
            {laden && <p className="mt-2 text-sm text-bf-muted">{t('training.goals.laden')}</p>}
            {!laden && verfuegbar.length === 0 && (
              // Kein Optimierer im Graphen ist der Normalzustand, kein Fehler — der Hinweis sagt,
              // was zu tun wäre, statt nur „leer" anzuzeigen.
              <p className="mt-2 text-sm text-bf-muted" data-testid="training-goals-empty">
                {t('training.goals.keineZiele')}
              </p>
            )}
            <ul className="mt-2 space-y-1">
              {verfuegbar.map((z) => (
                <li key={z.output}>
                  <label className="flex items-start gap-2 text-sm text-bf-white">
                    <input
                      type="checkbox"
                      className="mt-1"
                      data-testid={`training-goal-${z.output}`}
                      checked={gewaehlt.includes(z.output)}
                      onChange={() => umschalten(z.output)}
                    />
                    <span>
                      {zielBeschriftung(z, t)}
                      <span className="ml-1 text-xs text-bf-muted">({z.output})</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col">
            <label
              className="text-xs font-semibold uppercase tracking-wide text-bf-muted"
              htmlFor="training-goals-note"
            >
              {t('training.goals.begruendung')}
            </label>
            <p className="mt-1 text-xs text-bf-muted">{t('training.goals.begruendungHinweis')}</p>
            <textarea
              id="training-goals-note"
              data-testid="training-goals-note"
              className="mt-2 min-h-24 flex-1 rounded border border-bf-border bg-transparent p-2 text-sm text-bf-white"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                testId="training-goals-save"
                onClick={() => void speichern()}
                disabled={beschaeftigt || laden}
              >
                {t('training.goals.speichern')}
              </Button>
              <Button
                testId="training-goals-start"
                onClick={() => void trainingStarten()}
                disabled={beschaeftigt || laden || geaendert || begruendungFehlt}
              >
                {t('training.goals.trainingStarten')}
              </Button>
              <Button
                testId="training-goals-generate"
                onClick={() => void notebookGenerieren()}
                disabled={beschaeftigt || laden || geaendert}
              >
                {t('training.goals.notebookGenerieren')}
              </Button>
              {geaendert && (
                <span className="text-xs text-bf-muted" data-testid="training-goals-dirty">
                  {t('training.goals.ungespeichert')}
                </span>
              )}
              {begruendungFehlt && (
                <span
                  className="text-xs text-bf-gold"
                  data-testid="training-goals-note-required"
                >
                  {t('training.goals.begruendungPflicht')}
                </span>
              )}
            </div>
            {erzeugt && (
              // Der erzeugte Code wird GEZEIGT, bevor er laufen kann. Ein Knopf, der ungesehenen
              // Code auf einer Anlagen-Box ausführt, wäre der falsche Komfort.
              <div className="mt-3" data-testid="training-goals-preview">
                <p className="text-xs text-bf-muted">
                  {t('training.goals.vorschauHinweis', {
                    model: erzeugt.model,
                    sha: erzeugt.sha256.slice(0, 12),
                  })}
                </p>
                <pre className="mt-1 max-h-64 overflow-auto rounded border border-bf-border p-2 text-xs text-bf-white">
                  {erzeugt.codePreview}
                </pre>
                <Button
                  testId="training-goals-run"
                  onClick={() => void notebookAusfuehren()}
                  disabled={beschaeftigt}
                >
                  {t('training.goals.notebookAusfuehren')}
                </Button>
              </div>
            )}
            {meldung && (
              <p
                className={`mt-2 text-sm ${meldung.kind === 'ok' ? 'text-bf-white' : 'text-red-400'}`}
                data-testid="training-goals-message"
              >
                {meldung.text}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
